/**
 * Facts table for /trust. One source for the page and, later, llms.txt (T391), so the two cannot
 * disagree. Every row names its source; rows with `legalReview` must be checked by a lawyer before
 * the facts are treated as final (landing README decision 4, copy.md section 2.8).
 */

export interface FactSource {
  label: string
  /** Public page; omit for our own configuration, which has no public URL. */
  href?: string
}

export interface TrustFact {
  label: string
  value: string
  source: FactSource
  legalReview: boolean
}

export const TRUST_CHECKED = "28 Oct 2026"

export const ATO_RECORDS_SOURCE = {
  label: "ATO, record keeping overview",
  href: "https://www.ato.gov.au/businesses-and-organisations/preparing-lodging-and-paying/record-keeping-for-business/overview-of-record-keeping-rules-for-business",
} satisfies FactSource

/** Region name and the sentences built from it; copy elsewhere imports these instead of retyping. */
export const DATA_REGION = "Sydney" // Supabase database, ap-southeast-2
export const DATA_COUNTRY = "Australia"
/** Receipt files live in a Cloudflare R2 bucket created with a location hint, which is not a guarantee. */
export const FILES_LOCATION = `${DATA_COUNTRY} (Cloudflare location hint)`
export const STORAGE_SENTENCE = `Account data is stored in ${DATA_REGION}, ${DATA_COUNTRY}. Receipt files are stored in ${FILES_LOCATION}.`
export const OVERSEAS_SENTENCE = "A few services outside Australia process some data for us."

const CONFIG_SOURCE: FactSource = { label: "Our hosting and service configuration" }

export const TRUST_FACTS: readonly TrustFact[] = [
  {
    label: "Who provides Kestly",
    value: "Cave Sight (ABN 48 587 662 266), a sole trader business. Contact: hello@kestly.com.au.",
    source: CONFIG_SOURCE,
    legalReview: true,
  },
  {
    label: "Where data is stored",
    value: STORAGE_SENTENCE,
    source: CONFIG_SOURCE,
    legalReview: true,
  },
  {
    label: "Who else processes data",
    value: `${OVERSEAS_SENTENCE} They are listed in the second table below.`,
    source: CONFIG_SOURCE,
    legalReview: true,
  },
  {
    label: "How long records are kept",
    value:
      "The ATO asks you to keep most business records for 5 years. We do not promise to keep them that long for you. After a paid plan lapses we keep your data for 3 months at most, viewable and exportable, and remind you to export 60, 30, 14, 7 and 1 days before it is removed from your account.",
    source: ATO_RECORDS_SOURCE,
    legalReview: true,
  },
  {
    label: "Export",
    value: "Export trips, receipts and invoices as CSV or PDF at any time.",
    source: { label: "Kestly export feature" },
    legalReview: false,
  },
  {
    label: "Deleting your account",
    value:
      "Delete your account in the app under Settings, or on the web. It is scheduled for 30 days, then deleted.",
    source: { label: "Delete your account", href: "/legal/delete-account" },
    legalReview: false,
  },
]

export const PROCESSORS: readonly TrustFact[] = [
  {
    label: "Supabase",
    value: "Hosts our database and sign-in. Company based in the United States.",
    source: CONFIG_SOURCE,
    legalReview: true,
  },
  {
    label: "Resend",
    value:
      "Sends emails such as sign-in codes, and the messages you send us through the contact form. Company based in the United States.",
    source: CONFIG_SOURCE,
    legalReview: true,
  },
  {
    label: "Anthropic",
    value:
      "Reads receipt images and assistant messages, only when you use AI receipt reading or the assistant. Company based in the United States; processing happens outside Australia.",
    source: CONFIG_SOURCE,
    legalReview: true,
  },
  {
    label: "Google and Apple sign-in",
    value:
      "Planned for launch, not live yet. When you choose to sign in with Google or Apple, that company confirms your identity to us and shares your email address (Apple can hide it behind a private relay address). Companies based in the United States.",
    source: CONFIG_SOURCE,
    legalReview: true,
  },
  {
    label: "Stripe",
    value:
      "Will take web payments once web checkout launches (not live yet). Company based in the United States.",
    source: CONFIG_SOURCE,
    legalReview: true,
  },
  {
    label: "Apple and Google (on your phone)",
    value:
      "Show maps and turn the start and end of a trip into a suburb name, using the map and address services of your phone's operating system. Companies based in the United States.",
    source: CONFIG_SOURCE,
    legalReview: true,
  },
  {
    label: "Cloudflare",
    value: "Serves the website and runs our application code. Company based in the United States.",
    source: CONFIG_SOURCE,
    legalReview: true,
  },
]
